import React, { useContext, useRef, useState } from "react";
import {
  Space,
  Button,
  Input,
  InputRef,
  TableColumnType,
  DatePicker,
} from "antd";
import { FilterDropdownProps } from "antd/es/table/interface";
import Highlighter from "react-highlight-words";
import { SearchOutlined, FilterFilled } from "@ant-design/icons";
import dayjs from "dayjs";
import { UserContext } from "../../context";
import { TContext } from "../../types/config";

const { RangePicker } = DatePicker;

type DataIndex = string | number | symbol;

export const useColumnSearch = <T extends object>() => {
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef<InputRef>(null);

  const handleSearch = (
    selectedKeys: string[],
    confirm: FilterDropdownProps["confirm"],
    dataIndex: DataIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0] || "");
    setSearchedColumn(dataIndex.toString());
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText("");
  };

  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): Omit<TableColumnType<T>, "dataIndex" | "title"> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder="Filter by keyword"
          prefix={
            <SearchOutlined
              style={{ color: "rgba(0,0,0,.45)", marginRight: 4 }}
            />
          }
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{
            marginBottom: 8,
            display: "flex",
            paddingLeft: "12px",
          }}
        />
        <Space style={{ display: "flex", justifyContent: "space-between" }}>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            type="link"
            size="small"
            style={{ width: 45 }}
          >
            Reset
          </Button>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            size="small"
            style={{ width: 50 }}
          >
            Ok
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <FilterFilled
        style={{ color: filtered ? "var(--bs-primary)" : undefined }}
      />
    ),
    onFilter: (value, record) =>
      String((record as any)[dataIndex] ?? "")
        .toLowerCase()
        .includes(String(value ?? "").toLowerCase()),
    render: (text) =>
      searchedColumn === dataIndex.toString() ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: "var(--bs-primary)",
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const context: TContext = useContext(UserContext);

  const getDateRangeFilterProps = (dataIndex: DataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }: any) => (
      <div style={{ padding: 8 }}>
        <RangePicker
          format={context?.user?.dateFormat.toUpperCase()}
          value={selectedKeys[0] || []}
          onChange={(dates: any) => setSelectedKeys(dates ? [dates] : [])}
          style={{
            marginBottom: 8,
            display: "flex",
            paddingLeft: "12px",
          }}
        />
        <Space style={{ display: "flex", justifyContent: "space-between" }}>
          <Button
            onClick={() => {
              clearFilters?.();
              confirm();
            }}
            type="link"
            size="small"
            style={{ width: 45 }}
          >
            Reset
          </Button>
          <Button
            type="primary"
            onClick={() => confirm()}
            size="small"
            style={{ width: 50 }}
          >
            Ok
          </Button>
        </Space>
      </div>
    ),

    onFilter: (value: any, record: any) => {
      if (!value || value.length !== 2) return true;
      const [start, end] = value;
      const recordDate = dayjs(record[dataIndex]);
      return (
        recordDate.isSame(start, "day") ||
        recordDate.isSame(end, "day") ||
        (recordDate.isAfter(start, "day") && recordDate.isBefore(end, "day"))
      );
    },

    filterIcon: (filtered: boolean) => (
      <FilterFilled
        style={{ color: filtered ? "var(--bs-primary)" : undefined }}
      />
    ),
  });

  return { getColumnSearchProps, getDateRangeFilterProps };
};
